// Setup assistant, club admin side (SETUP_ASSISTANT_PLAN.md §5b): the network
// half. Availability per club (cached for the session so every entry point and
// the nav read one call), sessions, turns, proposals, the check page, apply and
// undo, plus the super-admin setting, spend and feature requests. Pure words
// live in utils/setupAssistant.js.
import {computed, ref} from 'vue';
import {useNuxtApp, useState} from '#app';
import {useHttpRequest} from '~/composables/useHttpRequest';
import {endpoints} from '~/network/endpoints';

/** The API's reason for a refusal, or a fallback; a cap arrives as {global, code}. */
export function setupApiError(e, fallback = 'Something went wrong. Please try again.') {
  if (!e) return fallback;
  if (typeof e === 'string') return e;
  if (Array.isArray(e.global) && e.global.length) return String(e.global[0]);
  if (e.title && typeof e.title === 'string') return e.title;
  if (e.message && typeof e.message === 'string' && !/^Unexpected token|JSON/.test(e.message)) return e.message;
  const firstField = Object.values(e).find(v => Array.isArray(v) && v.length);
  if (firstField) return String(firstField[0]);
  return fallback;
}

/**
 * Whether the club's admins see the entry points (S7: global switch, model,
 * the club's own switch, the viewer's role), the open sessions and saved
 * proposals. One call per club per page load; pages call `reload` after a
 * change. A 403/404 (not an admin, old API) reads as "not available".
 */
export function useSetupAssistantAvailability() {
  const state = useState('setupAssistantAvailability', () => ({clubId: null, data: null, loaded: false}));
  const {httpRequest} = useHttpRequest();
  const loading = ref(false);
  // X137 (R67 L1): one request per club at a time. The layout's watch and a page's onMounted both ask for a club's
  // availability before either has answered (a client-side move to another club's dashboard made two identical
  // calls); the second caller now shares the first call's promise. Per Nuxt app, so never across server requests.
  const nuxtApp = useNuxtApp();
  const inflight = nuxtApp._setupAssistantAvailabilityInflight ??= new Map();

  const load = async (clubId, {force = false} = {}) => {
    if (!clubId) return null;
    if (!force && state.value.loaded && state.value.clubId === clubId) return state.value.data;
    if (!force && inflight.has(clubId)) return inflight.get(clubId);
    const request = (async () => {
      loading.value = true;
      try {
        const data = await httpRequest(endpoints.setupAssistant.availability(clubId));
        state.value = {clubId, data, loaded: true};
        return data;
      } catch {
        state.value = {clubId, data: null, loaded: true};
        return null;
      } finally {
        loading.value = false;
        if (inflight.get(clubId) === request) inflight.delete(clubId);
      }
    })();
    inflight.set(clubId, request);
    return request;
  };
  const forClub = (clubId) => (state.value.loaded && state.value.clubId === clubId ? state.value.data : null);
  const enabled = computed(() => !!state.value.data?.enabled);
  return {availability: state, loadAvailability: load, availabilityFor: forClub, assistantEnabled: enabled, availabilityLoading: loading};
}

export function useSetupAssistant() {
  const {httpRequest} = useHttpRequest();
  const pending = ref(false);

  const call = async (endpoint, body) => {
    pending.value = true;
    try {
      return await httpRequest(endpoint, body ?? {});
    } finally {
      pending.value = false;
    }
  };

  // ── Sessions ──
  // X201 (R81 L9): peek = with no open conversation, the opening as a new one would have it, nothing saved (notStarted, no id);
  // the page starts the real session on the first message or answer
  const startSession = (clubId, focus, resume = true, peek = false) => call(endpoints.setupAssistant.start, {clubId, focus, resume, ...(peek ? {peek: true} : {})});
  const getSession = (id) => httpRequest(endpoints.setupAssistant.session(id));
  // X132: a pick card's answer carries the handles of the rows clicked; the message keeps their labels for the bubble.
  // X132: a settings card's Confirm carries every row's value by key (values).
  const sendMessage = (id, message, questionId = null, picked = null, values = null) =>
    call(endpoints.setupAssistant.send(id), {message, questionId: questionId || null, picked: Array.isArray(picked) && picked.length ? picked : null, values: values && typeof values === 'object' ? values : null});

  // ── Proposals ──
  const getProposal = (id) => httpRequest(endpoints.setupAssistant.proposal(id));
  const editStep = (id, stepId, body) => call(endpoints.setupAssistant.editStep(id, stepId), body);
  const check = (id) => httpRequest(endpoints.setupAssistant.check(id));
  // X200: the drafted scoring system as the scoring system detail, for the preview dialog
  const scoringPreview = (id, stepId) => httpRequest(endpoints.setupAssistant.scoringPreview(id, stepId));
  const apply = (id, tickedStepIds, stepUp = null) => call(endpoints.setupAssistant.apply(id), {tickedStepIds, stepUp: stepUp || null});
  const undo = (id) => call(endpoints.setupAssistant.undo(id));
  const discard = (id) => call(endpoints.setupAssistant.discard(id));
  const listProposals = (clubId) => httpRequest(endpoints.setupAssistant.list(clubId));
  // X132 item 7: "What I remember about your club"
  const listNotes = (clubId) => httpRequest(endpoints.setupAssistant.notes(clubId));
  const removeNote = (id) => call(endpoints.setupAssistant.removeNote(id));

  // ── Super admin ──
  const getSetting = () => httpRequest(endpoints.setupAssistant.setting);
  const setSetting = (modelKey) => call(endpoints.setupAssistant.setSetting, {modelKey});
  const spend = () => httpRequest(endpoints.setupAssistant.spend);
  const featureRequests = () => httpRequest(endpoints.setupAssistant.featureRequests);

  return {
    pending,
    startSession, getSession, sendMessage,
    getProposal, editStep, check, scoringPreview, apply, undo, discard, listProposals, listNotes, removeNote,
    getSetting, setSetting, spend, featureRequests,
  };
}
